import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, BookOpen, Pencil, FileText, Paperclip, Loader2, UtensilsCrossed } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, fmtErr, API } from "../../lib/api";

export default function MenusTab({ restaurant }) {
  const [menus, setMenus] = useState([]);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [seasonals, setSeasonals] = useState([]);
  const [smEditing, setSmEditing] = useState(null);

  const loadSetMenus = () => api.get(`/restaurants/${restaurant.id}/set-menus/manage`).then((r) => setSeasonals(r.data)).catch(() => {});

  const saveSetMenu = async (sm) => {
    try {
      const body = {
        ...sm, price: Number(sm.price) || 0, min_party: Number(sm.min_party) || 1,
        courses: typeof sm.courses === "string" ? sm.courses.split("\n").map((c) => c.trim()).filter(Boolean) : (sm.courses || []),
      };
      delete body.id;
      delete body.restaurant_id;
      delete body.created_at;
      if (sm.id) {
        await api.put(`/restaurants/${restaurant.id}/set-menus/${sm.id}`, body);
      } else {
        await api.post(`/restaurants/${restaurant.id}/set-menus`, body);
      }
      toast.success("Set menu saved");
      setSmEditing(null);
      loadSetMenus();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const toggleSetMenu = async (sm, active) => {
    try {
      await api.put(`/restaurants/${restaurant.id}/set-menus/${sm.id}`, { ...sm, active });
      toast.success(active ? `${sm.name} is live` : `${sm.name} hidden`);
      loadSetMenus();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const removeSetMenu = async (sid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/set-menus/${sid}`);
      toast.success("Set menu deleted");
      loadSetMenus();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const uploadFile = async (mid, file, inputEl) => {
    const fd = new FormData();
    fd.append("file", file);
    setUploading(mid);
    try {
      await api.post(`/restaurants/${restaurant.id}/menus/${mid}/file`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Menu file attached");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
    setUploading(null);
    if (inputEl) inputEl.value = "";
  };

  const removeFile = async (mid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/menus/${mid}/file`);
      toast.success("File removed");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const load = () => api.get(`/restaurants/${restaurant.id}/menus`).then((r) => setMenus(r.data)).catch(() => {});
  useEffect(() => { load(); loadSetMenus(); }, [restaurant.id]);

  const save = async () => {
    try {
      if (editing.id) {
        await api.put(`/restaurants/${restaurant.id}/menus/${editing.id}`, editing);
      } else {
        await api.post(`/restaurants/${restaurant.id}/menus`, editing);
      }
      toast.success("Menu saved");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const remove = async (mid) => {
    try {
      await api.delete(`/restaurants/${restaurant.id}/menus/${mid}`);
      toast.success("Menu deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const updateSection = (si, patch) => {
    const sections = editing.sections.map((s, i) => (i === si ? { ...s, ...patch } : s));
    setEditing({ ...editing, sections });
  };

  const updateItem = (si, ii, patch) => {
    const sections = editing.sections.map((s, i) =>
      i === si ? { ...s, items: s.items.map((it, j) => (j === ii ? { ...it, ...patch } : it)) } : s);
    setEditing({ ...editing, sections });
  };

  return (
    <div data-testid="menus-tab">
      <div className="flex items-center justify-between mb-6">
        <p className="text-sm text-muted-foreground">Menus appear on your public restaurant page.</p>
        <Button data-testid="add-menu-btn" size="sm" className="rounded-full"
          onClick={() => setEditing({ name: "", description: "", sections: [] })}>
          <Plus className="w-4 h-4 mr-2" /> New menu
        </Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {menus.map((m) => (
          <div key={m.id} data-testid={`menu-card-${m.id}`} className="rounded-2xl border border-border/60 bg-card p-6">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" /> {m.name}
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  {m.sections.length} sections · {m.sections.reduce((n, s) => n + s.items.length, 0)} items
                </p>
              </div>
              <div className="flex gap-2">
                <Button data-testid={`edit-menu-${m.id}`} size="sm" variant="outline" className="rounded-full h-8"
                  onClick={() => setEditing(JSON.parse(JSON.stringify(m)))}>
                  <Pencil className="w-3.5 h-3.5" />
                </Button>
                <Button data-testid={`delete-menu-${m.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                  onClick={() => remove(m.id)}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
            {m.description && <p className="text-sm text-muted-foreground mt-2">{m.description}</p>}
            <div className="mt-4 border-t border-border/40 pt-4">
              {m.file ? (
                <div className="flex items-center justify-between gap-3">
                  <a data-testid={`menu-file-view-${m.id}`} href={`${API}/menu-files/${m.file.path}`} target="_blank" rel="noreferrer"
                    className="text-sm text-primary hover:underline flex items-center gap-1.5 truncate">
                    <FileText className="w-4 h-4 shrink-0" /> {m.file.filename}
                  </a>
                  <button data-testid={`menu-file-remove-${m.id}`} onClick={() => removeFile(m.id)}
                    className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label data-testid={`menu-file-attach-${m.id}`}
                  className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer">
                  {uploading === m.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Paperclip className="w-4 h-4" />}
                  {uploading === m.id ? "Uploading..." : "Attach menu file (PDF/image)"}
                  <input data-testid={`menu-file-input-${m.id}`} type="file" accept=".pdf,image/png,image/jpeg,image/webp" className="hidden"
                    onChange={(e) => e.target.files?.[0] && uploadFile(m.id, e.target.files[0], e.target)} />
                </label>
              )}
            </div>
          </div>
        ))}
        {menus.length === 0 && (
          <p className="text-sm text-muted-foreground md:col-span-2" data-testid="no-menus-msg">No menus yet — create your first one.</p>
        )}
      </div>

      <div className="mt-10" data-testid="set-menus-section">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <div>
            <h3 className="font-outfit font-medium text-lg flex items-center gap-2">
              <UtensilsCrossed className="w-5 h-5 text-primary" /> Seasonal set menus & pre-orders
            </h3>
            <p className="text-xs text-muted-foreground mt-1">Fixed-price menus guests add when booking (Christmas, New Year...). Toggle them on/off each season — no need to delete.</p>
          </div>
          <Button data-testid="add-set-menu-btn" size="sm" className="rounded-full"
            onClick={() => setSmEditing({ name: "", description: "", price: "", courses: "", active: true, valid_from: "", valid_to: "", min_party: 1, prepay: false })}>
            <Plus className="w-4 h-4 mr-2" /> New set menu
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {seasonals.map((sm) => (
            <div key={sm.id} data-testid={`set-menu-card-${sm.id}`} className={`rounded-2xl border bg-card p-5 ${sm.active ? "border-border/60" : "border-border/30 opacity-70"}`}>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {sm.name}
                    {!sm.active && <Badge variant="outline" className="text-muted-foreground">inactive</Badge>}
                    {sm.prepay && <Badge variant="outline" className="text-primary border-primary/40">prepaid</Badge>}
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    £{Number(sm.price).toFixed(2)} pp · min party {sm.min_party}
                    {sm.valid_from || sm.valid_to ? ` · ${sm.valid_from || "any"} → ${sm.valid_to || "any"}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Switch data-testid={`set-menu-active-${sm.id}`} checked={sm.active} onCheckedChange={(v) => toggleSetMenu(sm, v)} />
                  <Button data-testid={`edit-set-menu-${sm.id}`} size="sm" variant="outline" className="rounded-full h-8"
                    onClick={() => setSmEditing({ ...sm, courses: (sm.courses || []).join("\n") })}>
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  <Button data-testid={`delete-set-menu-${sm.id}`} size="sm" variant="ghost" className="rounded-full h-8 text-destructive"
                    onClick={() => removeSetMenu(sm.id)}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
          {seasonals.length === 0 && (
            <p className="text-sm text-muted-foreground md:col-span-2" data-testid="no-set-menus-msg">No set menus yet — add your Christmas or New Year menu here.</p>
          )}
        </div>
      </div>

      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="bg-card border-border/60 max-w-2xl max-h-[85vh] overflow-y-auto">
          {editing && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">{editing.id ? "Edit menu" : "New menu"}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2" data-testid="menu-editor">
                <Input data-testid="menu-name-input" placeholder="Menu name (e.g. Dinner)" value={editing.name}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="menu-description-input" placeholder="Short description (optional)" value={editing.description}
                  onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="bg-background border-border/60" />

                {editing.sections.map((s, si) => (
                  <div key={si} className="rounded-xl border border-border/40 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                      <Input data-testid={`section-title-${si}`} placeholder="Section title (e.g. Starters)" value={s.title}
                        onChange={(e) => updateSection(si, { title: e.target.value })} className="bg-background border-border/60" />
                      <button data-testid={`remove-section-${si}`} onClick={() => setEditing({ ...editing, sections: editing.sections.filter((_, i) => i !== si) })}
                        className="text-muted-foreground hover:text-destructive transition-colors shrink-0">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    {s.items.map((it, ii) => (
                      <div key={ii} className="grid grid-cols-12 gap-2 items-center">
                        <Input data-testid={`item-name-${si}-${ii}`} placeholder="Item" value={it.name}
                          onChange={(e) => updateItem(si, ii, { name: e.target.value })}
                          className="bg-background border-border/60 col-span-4 h-9 text-sm" />
                        <Input data-testid={`item-price-${si}-${ii}`} placeholder="£0.00" value={it.price}
                          onChange={(e) => updateItem(si, ii, { price: e.target.value })}
                          className="bg-background border-border/60 col-span-2 h-9 text-sm" />
                        <Input data-testid={`item-desc-${si}-${ii}`} placeholder="Description" value={it.description}
                          onChange={(e) => updateItem(si, ii, { description: e.target.value })}
                          className="bg-background border-border/60 col-span-5 h-9 text-sm" />
                        <button data-testid={`remove-item-${si}-${ii}`}
                          onClick={() => updateSection(si, { items: s.items.filter((_, j) => j !== ii) })}
                          className="text-muted-foreground hover:text-destructive transition-colors col-span-1 flex justify-center">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <Button data-testid={`add-item-${si}`} size="sm" variant="outline" className="rounded-full h-8"
                      onClick={() => updateSection(si, { items: [...s.items, { name: "", price: "", description: "" }] })}>
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add item
                    </Button>
                  </div>
                ))}

                <Button data-testid="add-section-btn" variant="outline" className="rounded-full"
                  onClick={() => setEditing({ ...editing, sections: [...editing.sections, { title: "", items: [] }] })}>
                  <Plus className="w-4 h-4 mr-2" /> Add section
                </Button>
                <Button data-testid="save-menu-btn" onClick={save} disabled={!editing.name} className="w-full rounded-full">
                  Save menu
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!smEditing} onOpenChange={() => setSmEditing(null)}>
        <DialogContent className="bg-card border-border/60 max-w-xl max-h-[85vh] overflow-y-auto">
          {smEditing && (
            <>
              <DialogHeader><DialogTitle className="font-outfit">{smEditing.id ? "Edit set menu" : "New set menu"}</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-2" data-testid="set-menu-editor">
                <Input data-testid="set-menu-name-input" placeholder="Name (e.g. Christmas Day Menu)" value={smEditing.name}
                  onChange={(e) => setSmEditing({ ...smEditing, name: e.target.value })} className="bg-background border-border/60" />
                <Input data-testid="set-menu-description-input" placeholder="Short description (optional)" value={smEditing.description}
                  onChange={(e) => setSmEditing({ ...smEditing, description: e.target.value })} className="bg-background border-border/60" />
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Price per person (£)</Label>
                    <Input data-testid="set-menu-price-input" type="number" min="0" step="0.5" value={smEditing.price}
                      onChange={(e) => setSmEditing({ ...smEditing, price: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Min party size</Label>
                    <Input data-testid="set-menu-min-party-input" type="number" min="1" value={smEditing.min_party}
                      onChange={(e) => setSmEditing({ ...smEditing, min_party: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Valid from</Label>
                    <Input data-testid="set-menu-valid-from" type="date" value={smEditing.valid_from}
                      onChange={(e) => setSmEditing({ ...smEditing, valid_from: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                  <div>
                    <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">Valid until</Label>
                    <Input data-testid="set-menu-valid-to" type="date" value={smEditing.valid_to}
                      onChange={(e) => setSmEditing({ ...smEditing, valid_to: e.target.value })} className="mt-1.5 bg-background border-border/60" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs tracking-[0.15em] uppercase text-muted-foreground">What's included (one per line)</Label>
                  <Textarea data-testid="set-menu-courses-input" rows={4} value={smEditing.courses}
                    onChange={(e) => setSmEditing({ ...smEditing, courses: e.target.value })}
                    placeholder={"3 courses\nGlass of fizz on arrival\nCoffee & mince pies"} className="mt-1.5 bg-background border-border/60" />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <Label className="text-sm">Active (guests can add it when booking)</Label>
                  <Switch data-testid="set-menu-active-switch" checked={smEditing.active}
                    onCheckedChange={(v) => setSmEditing({ ...smEditing, active: v })} />
                </div>
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-sm">Require prepayment</Label>
                    <p className="text-[11px] text-muted-foreground mt-0.5">Guests pay the full menu price when booking. Needs your Stripe connected in Settings.</p>
                  </div>
                  <Switch data-testid="set-menu-prepay-switch" checked={smEditing.prepay}
                    onCheckedChange={(v) => setSmEditing({ ...smEditing, prepay: v })} />
                </div>
                <Button data-testid="save-set-menu-btn" onClick={() => saveSetMenu(smEditing)} disabled={!smEditing.name} className="w-full rounded-full">
                  Save set menu
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
